/**
 * <license header>
 */

import type { BearerTokenInfo } from './types';

/**
 * Utility class for extracting and handling Bearer tokens from HTTP request headers.
 * Supports both standard HTTP headers and OpenWhisk action parameter formats for maximum portability.
 */
class BearerToken {
  /**
   * Extracts the Bearer token from HTTP request headers and returns detailed token information.
   * Supports both standard HTTP headers and OpenWhisk action parameter formats.
   *
   * @param headersOrParams - Either a standard headers object or OpenWhisk action parameters
   * @returns Detailed token information object
   *
   * @example
   * // Standard HTTP headers approach
   * const headers = {
   *   authorization: 'Bearer abc123token'
   * };
   * const tokenInfo = BearerToken.extract(headers);
   *
   * @example
   * // OpenWhisk action parameters (backward compatibility)
   * const params = {
   *   __ow_headers: {
   *     authorization: 'Bearer abc123token'
   *   }
   * };
   * const tokenInfo = BearerToken.extract(params);
   *
   * @example
   * // Both return the same result:
   * // {
   * //   token: 'abc123token',
   * //   tokenLength: 11,
   * //   isValid: true,
   * //   expiry: '2024-01-01T12:00:00.000Z',
   * //   timeUntilExpiry: 3600000
   * // }
   */
  static extract(headersOrParams: { [key: string]: any }): BearerTokenInfo {
    let token: string | null = null;

    // Try standard headers approach first (more portable)
    if (headersOrParams.authorization?.startsWith('Bearer ')) {
      token = headersOrParams.authorization.substring('Bearer '.length);
    }
    // Fall back to OpenWhisk format for backward compatibility
    else if (headersOrParams.__ow_headers?.authorization?.startsWith('Bearer ')) {
      token = headersOrParams.__ow_headers.authorization.substring('Bearer '.length);
    }

    return BearerToken.info(token);
  }

  /**
   * Analyzes a Bearer token and returns detailed information including validity and expiry.
   * Supports both JWT tokens (with automatic expiry detection) and plain tokens (24h default expiry).
   *
   * @param token - The Bearer token string (or null). Can be JWT or plain token.
   * @returns Detailed token information object
   *
   * @example
   * // Plain token (gets 24h default expiry)
   * const plainTokenInfo = BearerToken.info('abc123token');
   * // returns: {
   * //   token: 'abc123token',
   * //   tokenLength: 11,
   * //   isValid: true,
   * //   expiry: '2024-01-02T12:00:00.000Z', // 24h from now
   * //   timeUntilExpiry: 86400000 // milliseconds until expiry
   * // }
   *
   * @example
   * // JWT token (automatic expiry detection from 'exp' or 'expires_in' claims)
   * const jwtToken = 'eyJhbGciOiJIUzI1NiJ9.eyJleHAiOjE3MDQ0Njc2MDB9.signature';
   * const jwtTokenInfo = BearerToken.info(jwtToken);
   * // returns: {
   * //   token: 'eyJhbGciOiJIUzI1NiJ9...',
   * //   tokenLength: 45,
   * //   isValid: true, // false if expired
   * //   expiry: '2024-01-05T12:00:00.000Z', // from JWT exp claim
   * //   timeUntilExpiry: 172800000 // actual time until expiry
   * // }
   *
   * @example
   * // Null or invalid token
   * const nullTokenInfo = BearerToken.info(null);
   * // returns: {
   * //   token: null,
   * //   tokenLength: 0,
   * //   isValid: false,
   * //   expiry: null,
   * //   timeUntilExpiry: null
   * // }
   */
  static info(token: string | null): BearerTokenInfo {
    const tokenExpiry = BearerToken._calculateExpiry(token);

    return {
      token: token,
      tokenLength: token ? token.length : 0,
      isValid: BearerToken._isTokenValid(token, tokenExpiry),
      expiry: tokenExpiry ? tokenExpiry.toISOString() : null,
      timeUntilExpiry: tokenExpiry ? Math.max(0, tokenExpiry.getTime() - Date.now()) : null,
    };
  }

  /**
   * Checks if the given token is valid and not expired
   * @private
   * @param token - The bearer token string
   * @param tokenExpiry - The token expiry date
   * @returns {boolean} True if token is valid
   */
  private static _isTokenValid(token: string | null, tokenExpiry: Date | null): boolean {
    if (!token) {
      return false;
    }

    if (tokenExpiry && Date.now() >= tokenExpiry.getTime()) {
      console.log('⏰ Token has expired');
      return false;
    }

    return true;
  }

  /**
   * Calculates token expiry from JWT token or uses default for non-JWT tokens
   * @private
   * @param token - The token string (JWT or plain token)
   * @returns Date object representing token expiry
   */
  private static _calculateExpiry(token: string | null): Date | null {
    // Handle empty tokens
    if (!token) {
      return null;
    }

    try {
      // Try to parse as JWT token
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(Buffer.from(parts[1] || '', 'base64').toString());

        if (payload.expires_in) {
          // expires_in is in milliseconds
          return new Date(Date.now() + parseInt(payload.expires_in));
        }

        if (payload.exp) {
          // exp is Unix timestamp in seconds
          return new Date(payload.exp * 1000);
        }
      }

      // For non-JWT tokens or JWT tokens without expiry, default to 24 hours
      return new Date(Date.now() + 24 * 60 * 60 * 1000);
    } catch (error) {
      console.warn('[WARN] Could not parse token expiry, using default 24h');
      return new Date(Date.now() + 24 * 60 * 60 * 1000);
    }
  }
}

export default BearerToken;
export type { BearerTokenInfo } from './types';
